import { useEffect, useState } from 'react';
import {
  Link as RouterLink,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add,
  CheckCircleOutline,
  DeleteOutline,
  DownloadOutlined,
  DragIndicator,
  EditOutlined,
  FlagOutlined,
  FolderOpenOutlined,
  Favorite,
  FavoriteBorder,
} from '@mui/icons-material';
import {
  useBlueprintQuery,
  useBlueprintVersionsQuery,
  useBlueprintVersionQuery,
  useBlueprintFavoriteSetMutation,
  useBlueprintDeleteMutation,
  useBlueprintReportMutation,
  type BlueprintFieldsFragment,
} from '@/generated/graphql';
import AppFooter from '@/common/components/layout/AppFooter';
import { BlueprintArtwork } from '@/common/components/blueprints/BlueprintArtwork';
import { BlueprintAudioPreview } from '@/common/components/blueprints/BlueprintAudioPreview';
import { BlueprintDownloadDialog } from '@/common/components/blueprints/BlueprintDownloadDialog';
import { useBlueprintLibrary } from '@/common/components/blueprints/useBlueprintLibrary';
import { COMMUNITY_LICENSE_URL } from '@/common/components/blueprints/communityPolicy';
import { parseBlueprintChangelog } from '@/common/components/blueprints/blueprintChangelog';
import { Mono, SectionLabel, useModeTokens } from '@/common/components/ui';
import { VersionHistoryButton } from '@/common/components/ui/VersionHistoryButton';

function ChangelogList({
  value,
  compact = false,
}: {
  value: string;
  compact?: boolean;
}) {
  const items = parseBlueprintChangelog(value);
  return (
    <Box
      component="ul"
      sx={{
        m: 0,
        pl: compact ? 0 : 2.75,
        listStyle: compact ? 'none' : undefined,
        display: 'grid',
        gap: compact ? 0.75 : 1.25,
        '& li::marker': { color: 'primary.main' },
      }}
    >
      {items.map((item, index) => (
        <Typography
          component="li"
          variant={compact ? 'body2' : 'body1'}
          key={`${index}:${item}`}
          sx={{
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            lineHeight: compact ? 1.6 : 1.75,
            pl: compact ? 0 : 0.5,
            position: compact ? 'relative' : undefined,
            '&::before': compact
              ? {
                  content: '""',
                  position: 'absolute',
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  left: -16,
                  top: '0.75em',
                }
              : undefined,
          }}
        >
          {item}
        </Typography>
      ))}
    </Box>
  );
}

function changelogCountLabel(value: string) {
  const count = parseBlueprintChangelog(value).length;
  return `${count} ${count === 1 ? 'change' : 'changes'}`;
}

export default function BlueprintPage() {
  const { blueprintId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const t = useModeTokens();
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const [tab, setTab] = useState('overview');
  const [dialog, setDialog] = useState<'remove' | 'report' | 'appeal' | null>(
    null
  );
  const [reason, setReason] = useState('');
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');
  const library = useBlueprintLibrary();
  const detail = useBlueprintQuery({
    variables: { id: blueprintId },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const history = useBlueprintVersionsQuery({
    variables: { id: blueprintId },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const { refetch: refreshBlueprint } = detail;
  const { refetch: refreshHistory } = history;

  useEffect(() => {
    const refresh = () => {
      void refreshBlueprint().catch(() => {});
      void refreshHistory().catch(() => {});
    };
    window.addEventListener('blueprint-library-changed', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('blueprint-library-changed', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [refreshBlueprint, refreshHistory]);

  const blueprint = detail.data?.blueprint;
  const versions = history.data?.blueprintVersions || [];
  const requested = params.get('version');
  const summary = requested
    ? versions.find((item) => String(item.version) === requested)
    : versions[0];
  const selection = useBlueprintVersionQuery({
    variables: { id: blueprintId, versionId: summary?._id || '' },
    skip: !summary,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const version =
    selection.data?.blueprintVersion?._id === summary?._id
      ? selection.data?.blueprintVersion
      : undefined;
  const selected: BlueprintFieldsFragment | undefined =
    blueprint && version
      ? {
          ...blueprint,
          pluginName: version.pluginName,
          pluginVersion: version.pluginVersion,
          dawVersion: version.dawVersion,
          requirements: version.requirements,
          stockOnly: version.stockOnly,
          fileName: version.fileName,
          thumbnailUrl: version.thumbnailUrl,
          previewUrl: version.previewUrl,
          waveForm: version.waveForm,
          sharingEnabled: version.sharingEnabled,
          revisionId: version._id,
          version: version.version,
        }
      : undefined;
  const local = library.copies.find(
    (record) =>
      (record.blueprintId || record.id) === blueprintId &&
      record.revisionId === summary?._id
  );
  const [favorite, favoriteState] = useBlueprintFavoriteSetMutation();
  const [remove, removeState] = useBlueprintDeleteMutation();
  const [report, reportState] = useBlueprintReportMutation();
  const busy = library.busy.includes(blueprintId);

  const select = (number: number) => {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set('version', String(number));
      return next;
    });
    if (!desktop) setTab('overview');
  };
  const retry = () => {
    void detail.refetch();
    void history.refetch();
    if (summary) void selection.refetch();
  };
  const perform = async () => {
    setActionError('');
    try {
      if (dialog === 'remove') {
        await remove({ variables: { id: blueprintId } });
        navigate('/blueprints');
      } else {
        await report({
          variables: {
            id: blueprintId,
            reason,
            appeal: dialog === 'appeal',
            versionId: summary?._id,
          },
        });
        setNotice(
          dialog === 'appeal'
            ? 'Your appeal was submitted.'
            : 'Your report was submitted for review.'
        );
        setDialog(null);
        setReason('');
      }
    } catch (error) {
      setActionError(error instanceof Error ? error.message : String(error));
    }
  };

  const versionRail = (variant: 'rail' | 'tab') => (
    <Box
      component={variant === 'rail' ? 'aside' : 'section'}
      aria-label="Blueprint version history"
      sx={{
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRight: variant === 'rail' ? '1px solid' : 0,
        borderColor: 'divider',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 3,
          minHeight: 56,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <SectionLabel>Version history · {versions.length}</SectionLabel>
      </Stack>
      {history.loading && (
        <LinearProgress aria-label="Loading version history" />
      )}
      {history.error && (
        <Alert
          severity="error"
          action={<Button onClick={() => void history.refetch()}>Retry</Button>}
          sx={{ m: 2 }}
        >
          Could not load version history.
        </Alert>
      )}
      <Box
        role="region"
        aria-label="Versions"
        tabIndex={0}
        sx={{
          overflowY: variant === 'rail' ? 'auto' : 'visible',
          minHeight: 0,
          overscrollBehaviorY: 'contain',
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: -2,
          },
        }}
      >
        <Box component="ul" sx={{ m: 0, p: 0 }}>
          {versions.map((item) => (
            <Box
              key={item._id}
              component="li"
              sx={{
                listStyle: 'none',
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <VersionHistoryButton
                version={
                  item.version === 1
                    ? { ...item, commitMessage: 'Initial release' }
                    : item
                }
                selected={item._id === summary?._id}
                latest={item._id === blueprint?.revisionId}
                expanded={item._id === summary?._id}
                detailsId={`blueprint-version-${item._id}-notes`}
                label={
                  item.version === 1
                    ? 'Initial release'
                    : changelogCountLabel(item.commitMessage)
                }
                onSelect={() => select(item.version)}
              />
              <Collapse
                in={item._id === summary?._id}
                timeout="auto"
                unmountOnExit
              >
                <Box
                  id={`blueprint-version-${item._id}-notes`}
                  sx={{
                    pl: 8.25,
                    pr: 3,
                    pt: 1.25,
                    pb: 2,
                    bgcolor: t.surfaces.fillActive,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, mb: 0.75 }}
                  >
                    Changelog
                  </Typography>
                  {item.version > 1 ? (
                    <ChangelogList value={item.commitMessage} compact />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Original published version.
                    </Typography>
                  )}
                  {library.copies.some(
                    (record) =>
                      (record.blueprintId || record.id) === blueprintId &&
                      record.revisionId === item._id
                  ) && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={0.75}
                      sx={{
                        mt: 2,
                        pt: 1.5,
                        borderTop: 1,
                        borderColor: 'divider',
                        color: t.colors.accentText,
                      }}
                    >
                      <CheckCircleOutline sx={{ fontSize: 15 }} />
                      <Typography variant="caption" color="inherit">
                        Saved on this computer
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Collapse>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  const versionNumber = version?.version;
  const isLatest = version?._id === blueprint?.revisionId;
  const canAddVersion =
    blueprint?.isOwner && blueprint.moderationState === 'active';

  return (
    <>
      <Box
        sx={{
          bgcolor: t.surfaces.canvas,
          minWidth: 0,
          height: { md: '100%' },
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            md: '340px minmax(0, 1fr)',
            xl: '360px minmax(0, 1fr)',
          },
        }}
      >
        {desktop && versionRail('rail')}
        <Box
          role="region"
          aria-label="Blueprint workspace"
          tabIndex={0}
          sx={{
            minWidth: 0,
            minHeight: 0,
            overflowY: { md: 'auto' },
            overscrollBehaviorY: 'contain',
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: -2,
            },
          }}
        >
          <Box
            component="header"
            sx={{
              px: { xs: 2, sm: 3, lg: 4 },
              pt: 2.5,
              pb: 2.5,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Breadcrumbs aria-label="Blueprint navigation" sx={{ mb: 2.5 }}>
              <Link
                component={RouterLink}
                to="/blueprints"
                color="text.secondary"
                underline="hover"
                sx={{ fontSize: 13 }}
              >
                Blueprints
              </Link>
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                {blueprint?.title || 'Loading…'}
              </Typography>
            </Breadcrumbs>
            {detail.loading && !blueprint ? (
              <Stack spacing={1.5} aria-label="Loading blueprint header">
                <Skeleton width="55%" height={56} />
                <Skeleton width="35%" />
                <Skeleton width="75%" />
              </Stack>
            ) : blueprint ? (
              <>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ sm: 'flex-start' }}
                  justifyContent="space-between"
                  gap={2}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      component="h1"
                      variant="h2"
                      sx={{ mb: 1, overflowWrap: 'anywhere' }}
                    >
                      {blueprint.title}
                    </Typography>
                    <Mono size={11} sx={{ display: 'block' }}>
                      {versions.length}{' '}
                      {versions.length === 1 ? 'VERSION' : 'VERSIONS'} ·{' '}
                      {blueprint.blueprintType === 'preset'
                        ? 'PLUG-IN PRESET'
                        : 'FX CHAIN'}{' '}
                      · {blueprint.downloadCount} DOWNLOADS
                    </Mono>
                  </Box>
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        blueprint.blueprintType === 'preset'
                          ? blueprint.pluginName || 'Plug-in preset'
                          : `Live ${blueprint.dawVersion}`
                      }
                    />
                    {blueprint.stockOnly && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label="Stock devices"
                      />
                    )}
                  </Stack>
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  Shared by{' '}
                  <Link
                    component={RouterLink}
                    to={`/blueprints?creator=${encodeURIComponent(blueprint.creatorId)}&creatorName=${encodeURIComponent(blueprint.creatorName)}`}
                    sx={{ fontWeight: 600 }}
                  >
                    {blueprint.creatorName}
                  </Link>
                </Typography>
                <Typography
                  sx={{
                    mt: 1.5,
                    maxWidth: 760,
                    whiteSpace: 'pre-wrap',
                    color: 'text.secondary',
                    lineHeight: 1.7,
                  }}
                >
                  {blueprint.description}
                </Typography>
                {selected && version && (
                  <>
                    <Stack
                      direction="row"
                      gap={1}
                      flexWrap="wrap"
                      alignItems="center"
                      sx={{ mt: 2.5 }}
                    >
                      {local ? (
                        <>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<DragIndicator />}
                            draggable
                            onDragStart={(event) => {
                              event.preventDefault();
                              library.drag(blueprintId, version._id);
                            }}
                          >
                            Drag into DAW
                          </Button>
                          <Tooltip title="Show in folder">
                            <IconButton
                              aria-label="Show in folder"
                              onClick={() =>
                                void library.reveal(blueprintId, version._id)
                              }
                            >
                              <FolderOpenOutlined />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<DownloadOutlined />}
                          disabled={busy || !selected.sharingEnabled}
                          onClick={() =>
                            void library.download(
                              selected,
                              version._id,
                              version.version
                            )
                          }
                        >
                          {busy
                            ? 'Downloading…'
                            : `Download v${version.version}`}
                        </Button>
                      )}
                      <Tooltip
                        title={
                          blueprint.isFavorite ? 'Remove favorite' : 'Favorite'
                        }
                      >
                        <span>
                          <IconButton
                            aria-label={
                              blueprint.isFavorite
                                ? 'Remove favorite'
                                : 'Favorite'
                            }
                            disabled={
                              favoriteState.loading || !blueprint.sharingEnabled
                            }
                            onClick={() =>
                              void favorite({
                                variables: {
                                  id: blueprintId,
                                  favorite: !blueprint.isFavorite,
                                },
                              }).catch((error) => setActionError(String(error)))
                            }
                          >
                            {blueprint.isFavorite ? (
                              <Favorite color="primary" />
                            ) : (
                              <FavoriteBorder />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                    <Box
                      role="status"
                      sx={{
                        mt: 2.5,
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        alignItems: 'baseline',
                      }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        Version {version.version}
                        {isLatest ? ' · Latest' : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Audio, requirements and downloads follow this version.
                      </Typography>
                      {!isLatest && versions[0] && (
                        <Button
                          size="small"
                          onClick={() => select(versions[0].version)}
                        >
                          View latest · v{versions[0].version}
                        </Button>
                      )}
                    </Box>
                  </>
                )}
              </>
            ) : null}
          </Box>

          <Box sx={{ minHeight: 480 }}>
            <Box
              component="nav"
              aria-label="Blueprint details navigation"
              sx={{
                px: { xs: 1, sm: 2 },
                borderBottom: 1,
                borderColor: 'divider',
                position: 'sticky',
                top: { xs: 56, md: 0 },
                zIndex: 3,
                bgcolor: t.surfaces.canvas,
              }}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <Tabs
                  value={tab}
                  variant="scrollable"
                  scrollButtons={false}
                  aria-label="Blueprint details"
                  onChange={(_, value) => setTab(value)}
                  sx={{ minHeight: 56, flex: 1 }}
                >
                  <Tab
                    value="overview"
                    label="Overview"
                    id="blueprint-tab-overview"
                    aria-controls="blueprint-panel-overview"
                    sx={{ minHeight: 56 }}
                  />
                  {!desktop && (
                    <Tab
                      value="versions"
                      label={`Versions${versions.length ? ` (${versions.length})` : ''}`}
                      id="blueprint-tab-versions"
                      aria-controls="blueprint-panel-versions"
                      sx={{ minHeight: 56 }}
                    />
                  )}
                </Tabs>
                {canAddVersion && (
                  <Button
                    component={RouterLink}
                    to={`/blueprints/${blueprintId}/versions/new`}
                    startIcon={<Add />}
                    size="small"
                    variant="contained"
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                  >
                    New version
                  </Button>
                )}
                {blueprint?.isOwner && (
                  <>
                    <Tooltip
                      title={
                        blueprint.license
                          ? 'Edit details'
                          : 'Review sharing terms'
                      }
                    >
                      <span>
                        <IconButton
                          component={RouterLink}
                          to={`/blueprints/${blueprintId}/edit`}
                          aria-label={
                            blueprint.license
                              ? 'Edit details'
                              : 'Review sharing terms'
                          }
                          disabled={blueprint.moderationState !== 'active'}
                          size="small"
                        >
                          <EditOutlined fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Remove blueprint">
                      <IconButton
                        aria-label="Remove blueprint"
                        color="error"
                        size="small"
                        onClick={() => setDialog('remove')}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
                {blueprint && (
                  <Tooltip
                    title={
                      blueprint.isOwner &&
                      blueprint.moderationState !== 'active'
                        ? 'Appeal decision'
                        : 'Report this version'
                    }
                  >
                    <IconButton
                      aria-label={
                        blueprint.isOwner &&
                        blueprint.moderationState !== 'active'
                          ? 'Appeal decision'
                          : 'Report this version'
                      }
                      size="small"
                      onClick={() => {
                        setActionError('');
                        setDialog(
                          blueprint.isOwner &&
                            blueprint.moderationState !== 'active'
                            ? 'appeal'
                            : 'report'
                        );
                      }}
                    >
                      <FlagOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>

            {!desktop && tab === 'versions' ? (
              <Box
                role="tabpanel"
                id="blueprint-panel-versions"
                aria-labelledby="blueprint-tab-versions"
              >
                {versionRail('tab')}
              </Box>
            ) : (
              <Box
                role="tabpanel"
                id="blueprint-panel-overview"
                aria-labelledby="blueprint-tab-overview"
                sx={{ p: { xs: 2, sm: 3, lg: 4 }, minWidth: 0 }}
              >
                {(detail.loading || selection.loading) && (
                  <LinearProgress
                    aria-label="Loading blueprint version"
                    sx={{ mb: 2 }}
                  />
                )}
                {(detail.error || selection.error) && (
                  <Alert
                    severity="error"
                    action={<Button onClick={retry}>Retry</Button>}
                    sx={{ mb: 2 }}
                  >
                    Could not load this blueprint version.
                  </Alert>
                )}
                {(library.error || actionError) && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {library.error || actionError}
                  </Alert>
                )}
                {notice && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {notice}
                  </Alert>
                )}
                {requested &&
                  !history.loading &&
                  !history.error &&
                  !summary && (
                    <Alert
                      severity="warning"
                      action={
                        <Button onClick={() => setParams({})}>
                          Latest version
                        </Button>
                      }
                    >
                      That version was not found.
                    </Alert>
                  )}
                {!selected ? (
                  !detail.error &&
                  !selection.error &&
                  (!requested || history.loading || summary) && (
                    <Stack spacing={2} aria-label="Loading blueprint">
                      <Skeleton height={300} variant="rounded" />
                      <Skeleton width="70%" />
                      <Skeleton width="45%" />
                    </Stack>
                  )
                ) : (
                  <>
                    {!selected.sharingEnabled && (
                      <Alert severity="warning" sx={{ mb: 3 }}>
                        {blueprint?.moderationReason ||
                          'This version is unavailable for community sharing.'}
                      </Alert>
                    )}
                    <Box
                      component="section"
                      aria-label={`Version ${versionNumber} preview and requirements`}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: 'minmax(0, 1fr)',
                          lg: 'minmax(0, 1.35fr) minmax(260px, .65fr)',
                        },
                        gap: { xs: 3, lg: 4 },
                        pb: 4,
                        borderBottom: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            overflow: 'hidden',
                            borderRadius: 2,
                            '& > *': { width: '100%' },
                          }}
                        >
                          <BlueprintArtwork
                            title={selected.title}
                            image={selected.thumbnailUrl}
                          />
                        </Box>
                        <SectionLabel sx={{ mt: 2.5, mb: 1.25 }}>
                          Listen · v{versionNumber}
                        </SectionLabel>
                        {selected.previewUrl ? (
                          <BlueprintAudioPreview
                            key={version!._id}
                            blueprint={selected}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {version?.legacy
                              ? 'No audio preview was attached to this original upload.'
                              : 'Audio preview is unavailable.'}
                          </Typography>
                        )}
                      </Box>
                      <Stack
                        spacing={2}
                        sx={{
                          borderLeft: { lg: 1 },
                          borderColor: { lg: 'divider' },
                          pl: { lg: 4 },
                          minWidth: 0,
                        }}
                      >
                        <SectionLabel>Made to work with</SectionLabel>
                        <Typography
                          variant="h6"
                          sx={{ overflowWrap: 'anywhere' }}
                        >
                          {selected.blueprintType === 'preset'
                            ? `${selected.pluginName} ${selected.pluginVersion}`
                            : `Ableton Live ${selected.dawVersion}`}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}
                          color="text.secondary"
                        >
                          {selected.requirements ||
                            (selected.stockOnly
                              ? 'Uses stock devices, as declared by the creator.'
                              : 'Check that you have the required plug-ins before downloading.')}
                        </Typography>
                        <Mono size={10}>
                          {selected.fileName.split('.').pop()?.toUpperCase()} ·
                          COMPATIBLE SOFTWARE REQUIRED
                        </Mono>
                        <Divider />
                        <Stack direction="row" gap={0.75} flexWrap="wrap">
                          {selected.tags.map((tag) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Stack>
                        {selected.license && (
                          <Link
                            href={COMMUNITY_LICENSE_URL}
                            target="_blank"
                            rel="noreferrer"
                            variant="caption"
                          >
                            Shared under CC0 · Community tools to build on
                          </Link>
                        )}
                      </Stack>
                    </Box>

                    <Box
                      component="section"
                      aria-label={
                        version!.version === 1
                          ? 'Initial release'
                          : `Version ${version!.version} changelog`
                      }
                      sx={{
                        py: 4,
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: 'minmax(0, 1fr)',
                          sm: '170px minmax(0, 1fr)',
                        },
                        gap: { xs: 2, sm: 4 },
                      }}
                    >
                      <Box>
                        <SectionLabel>
                          {version!.version === 1
                            ? 'Initial release · v1'
                            : `Changelog · v${version!.version}`}
                        </SectionLabel>
                        <Mono size={9} sx={{ display: 'block', mt: 1.25 }}>
                          PUBLISHED{' '}
                          {new Date(version!.createdAt).toLocaleDateString(
                            undefined,
                            { year: 'numeric', month: 'short', day: 'numeric' }
                          )}
                        </Mono>
                      </Box>
                      <Box
                        sx={{
                          borderLeft: '2px solid',
                          borderColor: 'primary.main',
                          pl: 2.5,
                          minWidth: 0,
                        }}
                      >
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          {version!.version === 1
                            ? 'The starting point'
                            : `What changed in version ${version!.version}`}
                        </Typography>
                        {version!.version > 1 ? (
                          <Box sx={{ maxWidth: 760 }}>
                            <ChangelogList value={version!.commitMessage} />
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Original Blueprint release. Version notes begin with
                            the next update.
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>
          <AppFooter />
        </Box>
      </Box>

      <BlueprintDownloadDialog library={library} />
      <Dialog
        open={!!dialog}
        onClose={() => setDialog(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {dialog === 'remove'
            ? 'Remove this blueprint?'
            : dialog === 'appeal'
              ? 'Appeal sharing restriction'
              : `Report version ${summary?.version || ''}`}
        </DialogTitle>
        <DialogContent>
          {dialog === 'remove' ? (
            <Typography>
              All versions will be removed from the community library.
              Previously downloaded copies and the permissions already granted
              under CC0 remain with their recipients.
            </Typography>
          ) : (
            <TextField
              autoFocus
              label="Details"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              multiline
              minRows={4}
              fullWidth
              sx={{ mt: 1 }}
              slotProps={{ htmlInput: { maxLength: 1800 } }}
            />
          )}
          {actionError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {actionError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button
            color={dialog === 'remove' ? 'error' : 'primary'}
            disabled={
              removeState.loading ||
              reportState.loading ||
              (dialog !== 'remove' && !reason.trim())
            }
            onClick={() => void perform()}
          >
            {dialog === 'remove' ? 'Remove' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
